import { useState } from 'react';
import { ChevronDown, ChevronRight, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { SitetrackerIcon } from '@/components/ui/icons/SitetrackerIcon';
import { ChorusIcon } from '@/components/ui/icons/ChorusIcon';
import { AgentTools } from '@/features/types/tools';

interface Tab {
  id: string;
  label: string;
  content: React.ReactNode;
  showLoadingWhileRunning?: boolean;
}

interface ToolCallContainerProps {
  toolName: AgentTools;
  icon?: React.ReactNode;
  title: string;
  isRunning: boolean;
  tabs: Tab[];
}

export function ToolCallContainer({
  toolName,
  icon,
  title,
  isRunning = false,
  tabs,
}: ToolCallContainerProps) {
  const { t } = useTranslation('prince');
  const [isExpanded, setIsExpanded] = useState(false);
  const [activeTabId, setActiveTabId] = useState<string | null>(tabs[0]?.id || null);

  const activeTab = tabs.find(tab => tab.id === activeTabId);

  let toolIcon;
  if (icon) {
    toolIcon = icon;
  } else {
    if (toolName.startsWith('sf_')) {
      toolIcon = <SitetrackerIcon />;
    } else if (toolName.startsWith('chorus_')) {
      toolIcon = <ChorusIcon />;
    }
  }

  return (
    <div className="bg-gray-50 rounded-lg flex flex-col w-[60%] [800px]:w-full">
      <button
        className="flex items-center gap-2.5 p-3.5 hover:bg-gray-200 transition-colors"
        onClick={() => setIsExpanded(!isExpanded)}
        title={isExpanded ? 'Collapse' : 'Expand'}
        data-testid="tool-call-expand-button"
      >
        {isExpanded ? (
          <ChevronDown className="w-4 h-4 text-gray-600" />
        ) : (
          <ChevronRight className="w-4 h-4 text-gray-600" />
        )}
        {toolIcon && <div className="w-5 h-5 flex items-center justify-center">{toolIcon}</div>}
        <span className="text-sm text-gray-900" data-testid="tool-call-title">
          {title}
        </span>
        {isRunning && (
          <Loader2
            className="w-3 h-3 text-gray-600 animate-spin"
            data-testid="tool-call-header-running-icon"
          />
        )}
      </button>

      {isExpanded && (
        <div className="mt-2 ml-20 mb-5 mr-14 p-4 bg-white border border-gray-200 rounded-lg">
          <div className="space-y-4">
            {/* Tabs */}
            <div className="border-b border-gray-200">
              <div className="flex space-x-8">
                {tabs.map(tab => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTabId(tab.id)}
                    className={`pb-3 px-1 text-sm font-medium transition-colors ${
                      activeTabId === tab.id
                        ? 'text-sky-900 border-b-2 border-sky-900'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                    data-testid={`tool-call-tab-${tab.id}-button`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Tab Content */}
            <div className="max-h-[500px] overflow-y-auto" data-testid="tool-call-tab-content">
              {activeTab?.showLoadingWhileRunning && isRunning ? (
                <div className="text-sm text-gray-600" data-testid="tool-call-tab-loading-state">
                  {t('toolCalls.running')}
                </div>
              ) : (
                activeTab?.content
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
